import { checkProp } from '@purple-dot/libraries/src/check-prop';
import { Interface } from '@purple-dot/libraries/src/interface';
import IncognitoModeError from './incognito-mode-error';
import SessionStorage from './session-storage';
import UserSession from './user-session';

const listenerInterface = new Interface(['loading', 'notLoading', 'showError']);

/**
 * Can be run between multiple iframes
 */
class ClientCheckout {
  constructor(windowData, checkoutBackend, sdkCheckoutUpdates, errorHandler) {
    this.windowData = windowData;
    this.checkoutBackend = checkoutBackend;
    this.sdkCheckoutUpdates = sdkCheckoutUpdates;
    this.errorHandler = errorHandler;

    checkProp(this, 'windowData');
    checkProp(this, 'checkoutBackend');
    checkProp(this, 'sdkCheckoutUpdates');
    checkProp(this, 'errorHandler');

    this.listeners = [];

    this.sessionStorage = new SessionStorage();
    this.userSession = new UserSession();
    this.persistedCheckout = new PersistedCheckout(checkoutBackend);
    this.request = AddRequest(
      checkoutBackend,
      windowData,
      this.userSession,
      this.persistedCheckout,
      sdkCheckoutUpdates
    );

    sdkCheckoutUpdates.onChangeShownToUser((d) => this._onCheckoutShown(d));
  }

  frameLoaded() {
    try {
      if (this.windowData.checkoutId) {
        this.sessionStorage.setItem(
          'serverCheckoutId',
          JSON.stringify(this.windowData.checkoutId)
        );
      } else {
        this.sessionStorage.removeItem('serverCheckoutId');
      }
    } catch (err) {
      if (err instanceof IncognitoModeError) {
        // Ignore incognito errors here, they will be thrown again and handled
        // when the user takes an action
        return;
      }
      throw err;
    }
  }

  async add(lineItemRequest) {
    for (const listener of this.listeners) {
      listener.loading();
    }

    try {
      await this.request.execute(lineItemRequest);
    } catch (err) {
      if (err instanceof IncognitoModeError) {
        this._handleIncognitoError();
        return;
      }

      if (err.message.includes('is now sold out')) {
        this._handleSoldOutError(err);
        return;
      }

      if (err.message.includes('can only add')) {
        this._handleQuantityLimitError(err);
        return;
      }

      if (err.message.includes('already have all available units')) {
        this._handleQuantityLimitError(err);
        return;
      }

      if (
        err.message ===
        "We're sorry there are currently too many people trying to check out. Please try again in 10 minutes."
      ) {
        this._handleShieldsUpError(err);
        return;
      }

      if (
        err.message ===
        "We're sorry there are currently too many people trying to check out. Please try again in 10 minutes."
      ) {
        return;
      }

      if (!err.message.match(/is now sold out|unable to find shipping rate/)) {
        this.errorHandler.captureException(err);
      }

      // We log the error to the console for easier debugging
      // in case any unexpected errors happen
      // biome-ignore lint/suspicious/noConsole: log
      console.error(err);
      this._handleUnrecognisedError();
    }
  }

  attachListener(listener) {
    const isListener = listenerInterface.check(listener);
    if (!isListener) {
      throw new Error('Could not attach listener to checkoutSession');
    }

    this.listeners.push(listener);
  }

  _handleIncognitoError() {
    const errToSend = {
      message:
        "Oops! Look like you're in incognito mode in your browser. Please turn off incognito to proceed with your order.",
      isUserRetryable: false,
    };
    for (const listener of this.listeners) {
      listener.showError(errToSend);
    }
  }

  _handleSoldOutError(err) {
    const isUserRetryable = err.data?.isUserRetryable;
    for (const listener of this.listeners) {
      listener.showError({ message: err.message, isUserRetryable });
    }
  }

  _handleQuantityLimitError(err) {
    const isUserRetryable = err.data?.isUserRetryable;
    for (const listener of this.listeners) {
      listener.showError({ message: err.message, isUserRetryable });
    }
  }

  _handleShieldsUpError(err) {
    const isUserRetryable = err.data?.isUserRetryable;
    for (const listener of this.listeners) {
      listener.showError({ message: err.message, isUserRetryable });
    }
  }

  _handleUnrecognisedError() {
    // We don't know what has gone wrong at this point
    // so we reset the cart and ask the user to refresh the page
    // so the backend can resync
    this.persistedCheckout.clear();
    const errToSend = {
      message:
        'Oops! Small hiccup adding that to your cart. Please refresh the page and try again.',
      isUserRetryable: true,
    };
    for (const listener of this.listeners) {
      listener.showError(errToSend);
    }
  }

  _onCheckoutShown() {
    for (const listener of this.listeners) {
      listener.notLoading();
    }
  }
}

class PersistedCheckout {
  constructor(checkoutBackend) {
    this.checkoutBackend = checkoutBackend;
    this.sessionStorage = new SessionStorage();

    checkProp(this, 'checkoutBackend');
  }

  async id() {
    await this._ensureCacheInSync();

    return this._locallyStoredId();
  }

  async isPopulated() {
    const id = await this.id();
    return !!id;
  }

  update(data) {
    this.sessionStorage.setItem('checkoutSession', JSON.stringify(data));
    this.sessionStorage.setItem(
      'serverCheckoutId',
      JSON.stringify(this._extractId(data))
    );
  }

  clear() {
    this.sessionStorage.removeItem('checkoutSession');
    this.sessionStorage.removeItem('serverCheckoutId');
  }

  async _ensureCacheInSync() {
    if (!this._serverCheckoutId()) {
      this.sessionStorage.removeItem('checkoutSession');
    } else if (this._serverCheckoutId() !== this._locallyStoredId()) {
      try {
        const data = await this.checkoutBackend.fetch(this._serverCheckoutId());
        if (data.state === 'COMPLETED') {
          this.clear();
        } else {
          this.update(data);
        }
      } catch (err) {
        if (err.serverSide) {
          this.clear();
        } else {
          throw err;
        }
      }
    }
  }

  _locallyStoredId() {
    const value = this.sessionStorage.getItem('checkoutSession');
    if (!value) {
      return null;
    }
    return this._extractId(JSON.parse(value));
  }

  _serverCheckoutId() {
    const val = this.sessionStorage.getItem('serverCheckoutId');
    if (!val) {
      return null;
    }
    return JSON.parse(val);
  }

  _extractId(checkoutData) {
    const { checkoutId, id } = checkoutData;
    return checkoutId || id;
  }
}

function AddRequest(
  checkoutApi,
  windowData,
  userSession,
  persistedCheckout,
  sdkCheckoutUpdates
) {
  if (windowData.enableCartFlag) {
    return new SeparateCartAddRequest(
      checkoutApi,
      persistedCheckout,
      userSession,
      sdkCheckoutUpdates,
      windowData.discountCode
    );
  }
  return new ExpressCheckoutAddRequest(
    checkoutApi,
    persistedCheckout,
    userSession,
    sdkCheckoutUpdates,
    windowData.discountCode
  );
}

class SeparateCartAddRequest {
  constructor(
    checkoutBackend,
    persistedCheckout,
    userSession,
    sdkCheckoutUpdates,
    autoDiscount
  ) {
    this.checkoutBackend = checkoutBackend;
    this.persistedCheckout = persistedCheckout;
    this.sdkCheckoutUpdates = sdkCheckoutUpdates;
    this.userSession = userSession;
    this.autoDiscount = autoDiscount;
  }

  async execute(lineItemRequest) {
    const id = await this.persistedCheckout.id();
    if (id) {
      await this._addLineItem(id, lineItemRequest);
    } else {
      const data = await this.checkoutBackend.create({
        userSession: this.userSession,
        lineItemRequest,
        discountCode: this.autoDiscount,
      });
      this.persistedCheckout.update(data);

      const lineItem = data.lineItems[data.lineItems.length - 1];
      this.sdkCheckoutUpdates.checkoutCreated({
        checkoutId: data.checkoutId,
        cartEnabled: true,
        lineItemRequest,
        lineItem,
      });
    }
  }

  async _addLineItem(id, lineItemRequest) {
    const data = await this.checkoutBackend.addLineItem(id, lineItemRequest);
    this.persistedCheckout.update(data);

    const lineItem = data.lineItems[data.lineItems.length - 1];
    this.sdkCheckoutUpdates.lineItemAdded({
      checkoutId: data.checkoutId,
      cartEnabled: true,
      lineItemRequest,
      lineItem,
    });
  }
}

class ExpressCheckoutAddRequest {
  constructor(
    checkoutBackend,
    persistedCheckout,
    userSession,
    sdkCheckoutUpdates,
    autoDiscount
  ) {
    this.checkoutBackend = checkoutBackend;
    this.persistedCheckout = persistedCheckout;
    this.userSession = userSession;
    this.sdkCheckoutUpdates = sdkCheckoutUpdates;
    this.autoDiscount = autoDiscount;

    if (
      !(
        this.checkoutBackend &&
        this.persistedCheckout &&
        this.userSession &&
        this.sdkCheckoutUpdates
      )
    ) {
      throw new Error('Could not create ExpressCheckoutAddRequest');
    }
  }

  async execute(lineItemRequest) {
    const haveCheckoutLocally = await this.persistedCheckout.isPopulated();
    let data;

    /*
     * In Express Checkout we only let the user have 1 item
     * If they have an item we replace it, we replace it rather
     * than making a new checkout as we do not want them to have
     * to enter their details again
     */

    if (haveCheckoutLocally) {
      const id = await this.persistedCheckout.id();
      data = await this.checkoutBackend.replace(id, lineItemRequest);
    } else {
      data = await this.checkoutBackend.create({
        userSession: this.userSession,
        lineItemRequest,
        discountCode: this.autoDiscount,
      });
    }
    this.persistedCheckout.update(data);

    const lineItem = data.lineItems[data.lineItems.length - 1];
    this.sdkCheckoutUpdates.checkoutCreated({
      checkoutId: data.checkoutId,
      cartEnabled: false,
      lineItemRequest,
      lineItem,
    });
  }
}

export default ClientCheckout;
export { PersistedCheckout, listenerInterface };
